import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui/primitives';
import { ErrorState, LoadingState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { getSeoAutopilot, resumeSeoAutopilotListing, saveSeoAutopilotSettings } from '@/data/seo-autopilot';
import {
  CADENCE_LABELS, CADENCE_LIMITS, STOP_LABELS, isArchiveId, settingsError, settingsPatch, tierLabel, toggleIn,
  type SeoAutopilotCadence, type SeoAutopilotSettings,
} from '@/data/seo-autopilot-model';

const KEY = ['seo-autopilot'] as const;

function NumberField({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 text-xs text-text-secondary">
      <span>{label}</span>
      <input
        type="number" inputMode="numeric" min={min} max={max} step={1} value={Number.isFinite(value) ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
        className="w-20 rounded-md border border-border-default bg-surface px-2 py-1 text-right text-sm text-text-primary"
      />
    </label>
  );
}

/** TODAY'S SEO autopilot panel, byte-for-byte as deployed at 6405cc7 (only the export name changed). Shown while content_library_v1 is OFF (Delta UI IVG GATE 25-10-2026). */
export function SeoAutopilotPanelLegacy() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const status = useQuery({ queryKey: KEY, queryFn: getSeoAutopilot, enabled: open });
  const [draft, setDraft] = useState<SeoAutopilotSettings | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { if (status.data) setDraft(status.data.settings); }, [status.data]);

  const save = useMutation({
    mutationFn: (patch: Partial<SeoAutopilotSettings>) => saveSeoAutopilotSettings(patch),
    onSuccess: (res) => { qc.setQueryData(KEY, res); setDraft(res.settings); setMsg('Saved.'); },
    onError: (e) => setMsg(e instanceof ApiError ? `Not saved: ${e.message}` : 'Not saved. Try again.'),
  });

  const resume = useMutation({
    mutationFn: (listingId: string) => resumeSeoAutopilotListing(listingId),
    onSuccess: (res) => { qc.setQueryData(KEY, res); setMsg(`Listing ${res.resumed} resumed.`); },
    onError: (e) => setMsg(e instanceof ApiError ? `Not resumed: ${e.message}` : 'Not resumed. Try again.'),
  });

  const s = status.data;
  const err = draft ? settingsError(draft) : null;
  const patch = s && draft ? settingsPatch(s.settings, draft) : {};
  const dirty = Object.keys(patch).length > 0;
  const setCad = (k: keyof SeoAutopilotCadence, v: number) => draft && setDraft({ ...draft, cadence: { ...draft.cadence, [k]: v } });

  return (
    <section className="rounded-xl border border-border-subtle bg-surface p-4" aria-label="SEO autopilot settings">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-base font-semibold text-text-primary">SEO autopilot</h3>
          <p className="text-xs text-text-secondary">Writes blog drafts for each eligible partner listing and saves them as WordPress drafts. Nothing is published without you.</p>
        </div>
        <Button type="button" variant="secondary" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide settings' : 'Settings'}</Button>
      </div>
      {open && status.isLoading && <LoadingState rows={3} />}
      {open && status.isError && <ErrorState error={status.error} onRetry={() => void status.refetch()} />}
      {open && s && draft && (
        <div className="mt-4 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={s.running ? 'success' : 'warning'}>{s.running ? 'Running (drafts only)' : 'Stopped'}</Badge>
            {s.stops.map((x) => <Badge key={x} tone="warning">{STOP_LABELS[x] ?? x}</Badge>)}
            <label className="ml-auto flex items-center gap-2 text-xs text-text-primary">
              <input type="checkbox" checked={draft.global_stop} onChange={(e) => setDraft({ ...draft, global_stop: e.target.checked })} />
              Stop the autopilot
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium text-text-primary">Volume caps</legend>
              <label className="flex items-center justify-between gap-3 text-xs text-text-secondary">
                <span>Daily cap follows the listings{s.schedule ? ` (${s.schedule.daily_auto} a day for ${s.schedule.weekly_quota} a week)` : ''}</span>
                <input type="checkbox" checked={draft.caps.daily_auto} onChange={(e) => setDraft({ ...draft, caps: { ...draft.caps, daily_auto: e.target.checked } })} />
              </label>
              {!draft.caps.daily_auto && <NumberField label="Max posts per day" value={draft.caps.per_day} min={0} max={5000} onChange={(v) => setDraft({ ...draft, caps: { ...draft.caps, per_day: v } })} />}
              <NumberField label="Max posts per week" value={draft.caps.per_week} min={0} max={5000} onChange={(v) => setDraft({ ...draft, caps: { ...draft.caps, per_week: v } })} />
              <NumberField label="Max posts per month" value={draft.caps.per_month} min={0} max={5000} onChange={(v) => setDraft({ ...draft, caps: { ...draft.caps, per_month: v } })} />
            </fieldset>
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium text-text-primary">Cadence by Google position</legend>
              {(Object.keys(CADENCE_LIMITS) as Array<keyof SeoAutopilotCadence>).map((k) => (
                <NumberField key={k} label={CADENCE_LABELS[k]} value={draft.cadence[k]} min={CADENCE_LIMITS[k][0]} max={CADENCE_LIMITS[k][1]} onChange={(v) => setCad(k, v)} />
              ))}
            </fieldset>
          </div>

          {s.schedule && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-text-primary">Next 7 days — {s.schedule.daily_effective} draft{s.schedule.daily_effective === 1 ? '' : 's'} a day at most, {s.schedule.weekly_quota} a week</p>
              {s.schedule.warnings.map((w) => <p key={w} className="text-xs text-danger" role="status">{w}</p>)}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-7">
                {s.schedule.preview.map((d) => (
                  <div key={d.date} className="rounded-lg border border-border-subtle p-2 text-xs">
                    <p className="font-medium text-text-primary">{d.day} {d.date.slice(8, 10)}/{d.date.slice(5, 7)}</p>
                    <ul className="mt-1 space-y-1">
                      {d.items.map((it) => (
                        <li key={it.listing_id} className="text-text-secondary">
                          <span className="block truncate text-text-primary">{isArchiveId(it.listing_id) ? 'Category page: ' : ''}{it.title}</span>
                          {it.primary && <span className="block">“{it.primary}”</span>}
                        </li>
                      ))}
                      {d.items.length === 0 && <li className="text-text-secondary">—</li>}
                    </ul>
                  </div>
                ))}
              </div>
              {s.tracking && <p className="text-xs text-text-secondary">Rank tracking: {s.tracking.added} listing keyword{s.tracking.added === 1 ? '' : 's'} added (limit {s.tracking.max}).</p>}
            </div>
          )}

          {s.cost_estimate && (
            <div className="rounded-lg border border-border-subtle p-3 text-xs text-text-secondary">
              <p className="font-medium text-text-primary">{s.cost_estimate.label}</p>
              <p>Per article {s.cost_estimate.per_article ?? '—'} (worst case {s.cost_estimate.per_article_worst ?? '—'}) · per week {s.cost_estimate.per_week ?? '—'} · per month {s.cost_estimate.per_month ?? '—'} (worst {s.cost_estimate.per_month_worst ?? '—'})</p>
              <p>{s.cost_estimate.eligible_listings} active listings · about {s.cost_estimate.posts_per_week} posts a week, {s.cost_estimate.posts_per_month} a month at the saved settings. Based on {s.cost_estimate.basis}. Keyword/SERP data: {s.cost_estimate.evidence_api}.{s.cost_estimate.fixed_weekly ? ` Fixed: ${s.cost_estimate.fixed_weekly}.` : ''}</p>
            </div>
          )}

          <div>
            <p className="mb-1 text-sm font-medium text-text-primary">Listings{s.plan_at ? ` (checked ${new Date(s.plan_at).toLocaleString('en-GB')})` : ''}</p>
            <ul className="divide-y divide-border-subtle text-xs">
              {s.listings.map((l) => {
                const lp = draft.paused_listings.includes(l.listing_id);
                const tp = l.tenant_slug !== '' && draft.paused_tenants.includes(l.tenant_slug);
                return (
                  <li key={l.listing_id} className="flex flex-wrap items-center gap-2 py-2">
                    <span className="min-w-0 flex-1 truncate text-text-primary">{l.listing_title}</span>
                    {l.eligible ? <span className="text-text-secondary">“{l.keyword}” · {tierLabel(l.tier, draft.cadence)}{l.position ? ` · pos ${l.position}` : ''}</span>
                      : <Badge tone="neutral">{(l.reason ?? 'not eligible').replace(/_/g, ' ')}</Badge>}
                    {l.auto_paused && (
                      <>
                        <Badge tone="warning">Auto-paused</Badge>
                        {l.auto_paused_reason && <span className="text-text-secondary">{l.auto_paused_reason.replace(/_/g, ' ')}</span>}
                        <Button type="button" variant="secondary" disabled={resume.isPending} onClick={() => { setMsg(null); resume.mutate(l.listing_id); }}>Resume</Button>
                      </>
                    )}
                    <label className="flex items-center gap-1"><input type="checkbox" checked={lp} onChange={() => setDraft({ ...draft, paused_listings: toggleIn(draft.paused_listings, l.listing_id) })} />Pause listing</label>
                    {l.tenant_slug !== '' && <label className="flex items-center gap-1"><input type="checkbox" checked={tp} onChange={() => setDraft({ ...draft, paused_tenants: toggleIn(draft.paused_tenants, l.tenant_slug) })} />Pause {l.tenant_slug}</label>}
                  </li>
                );
              })}
              {s.listings.length === 0 && <li className="py-2 text-text-secondary">No listing check has run yet.</li>}
            </ul>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-text-secondary" title="Prepared for after the review trial; not available yet">
              <input type="checkbox" checked={false} disabled aria-disabled="true" />
              Full automatic publishing (off — available after your trial review)
            </label>
            <div className="ml-auto flex items-center gap-2">
              {(err || msg) && <span className={err ? 'text-xs text-danger' : 'text-xs text-text-secondary'} role="status">{err ?? msg}</span>}
              <Button type="button" variant="ghost" disabled={!dirty || save.isPending} onClick={() => { setDraft(s.settings); setMsg(null); }}>Reset</Button>
              <Button type="button" variant="primary" disabled={!dirty || !!err || save.isPending} onClick={() => { setMsg(null); save.mutate(patch); }}>{save.isPending ? 'Saving…' : 'Save settings'}</Button>
            </div>
          </div>

          {s.alerts.length > 0 && (
            <div className="text-xs">
              <p className="mb-1 font-medium text-text-primary">Needs attention</p>
              <ul className="space-y-1 text-text-secondary">{s.alerts.slice(0, 8).map((a, i) => <li key={i}>{a.type.replace(/_/g, ' ')}{a.listing_id ? ` · listing ${a.listing_id}` : ''}: {a.detail}</li>)}</ul>
            </div>
          )}
          {s.audit.length > 0 && (
            <details className="text-xs text-text-secondary"><summary className="cursor-pointer">Change history</summary>
              <ul className="mt-1 space-y-1">{s.audit.map((a, i) => <li key={i}>{a.at} · {a.principal} · {Object.keys(a.changes).join(', ')}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
